import React, { useMemo } from 'react';
import { ChartCard, generateChartColors, InfoTooltip, PieChart } from 'ui-v2';

import { type FragmentType, gql, useFragment } from '@/generated';
import { useChartTranslations } from '@/hooks/useChartTranslations';
import orderBy from 'lodash/orderBy';

export const Index_PresenceOfCertificationsFragment = gql(/* GraphQL */ `
  fragment Index_PresenceOfCertificationsFragment on ForestryIndicators {
    hasCertifications {
      items {
        count
        label
        pct
      }
      isDataSuppressed
    }
  }
`);

type PresenceOfCertificationsProps = {
  className?: string;
  indicators: FragmentType<typeof Index_PresenceOfCertificationsFragment>;
};

export function PresenceOfCertifications({ indicators, className }: PresenceOfCertificationsProps) {
  const { hasCertifications } = useFragment(Index_PresenceOfCertificationsFragment, indicators);

  const { title, subtitle, chartTooltip, getOptionLabel, getOptionTooltip, getOptionDisplayValue } =
    useChartTranslations('precenseOfCertifications');

  const pieData = useMemo(() => {
    const colors = generateChartColors(hasCertifications.items.length);
    const sorted = orderBy(
      hasCertifications.items
        .filter((item) => !!item)
        .map((item, index) => ({
          label: getOptionLabel(item?.label),
          tooltipLabel: getOptionTooltip(item?.label),
          value: item?.pct,
          color: colors[index],
          displayValue: getOptionDisplayValue(item.count, {
            pct: item.pct.toLocaleString('en', { maximumFractionDigits: 1 }),
          }),
        })),
      ['value'],
      ['desc']
    );
    return sorted.map((x, i) => ({ ...x, color: colors[i] }));
  }, [hasCertifications.items]);

  return (
    <ChartCard className={className}>
      <ChartCard.Header>
        <ChartCard.Title>
          {title}
          {chartTooltip && <InfoTooltip className="ml-1">{chartTooltip}</InfoTooltip>}
        </ChartCard.Title>
        {subtitle && <ChartCard.Subtitle>{subtitle}</ChartCard.Subtitle>}
      </ChartCard.Header>
      <ChartCard.Body isDataSuppressed={hasCertifications.isDataSuppressed}>
        <PieChart data={pieData} showLegend chartPosition="left" />
      </ChartCard.Body>
    </ChartCard>
  );
}
